import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  RedoBadge,
  type RedoBadgeColor,
  type RedoBadgeSize,
  type RedoBadgeType,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { memo, useContext } from "react";

const LOT_COLORS: RedoBadgeColor[] = [
  "brand",
  "success",
  "warning",
  "grayBlue",
  "blueLight",
  "blue",
  "indigo",
  "purple",
  "pink",
  "orange",
];

function hashLotColor(lotNumber: string): RedoBadgeColor {
  let hash = 5381;
  for (let i = 0; i < lotNumber.length; i++) {
    hash = ((hash << 5) + hash + lotNumber.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % LOT_COLORS.length;
  return LOT_COLORS[index] ?? "brand";
}

function formatExpiration(d: Date | string | null | undefined): string | null {
  if (!d) {
    return null;
  }
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) {
    return null;
  }
  return dt.toISOString().slice(0, 10);
}

/** Read the merchant's lot-expiration buffer from team settings. */
export function useLotExpirationBufferDays(): number {
  const team = useContext(TeamContext);
  return Math.max(
    0,
    Math.floor(
      team?.settings?.outboundLabels?.wms?.pickingDisabledPeriodDays ?? 0,
    ),
  );
}

/**
 * Treat as expired when expirationDate <= now + bufferDays.
 * Matches sync-inventory-to-shopify's pickingDisabledPeriodDays semantics.
 */
export function isLotExpired(
  d: Date | string | null | undefined,
  bufferDays: number,
): boolean {
  if (!d) {
    return false;
  }
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) {
    return false;
  }
  const cutoffMs = Date.now() + bufferDays * 24 * 60 * 60 * 1000;
  return dt.getTime() <= cutoffMs;
}

export const LotBadge = memo(function LotBadge({
  lotNumber,
  expirationDate,
  quantity,
  size = "xs",
  type = "primary",
}: {
  lotNumber: string;
  expirationDate?: Date | string | null;
  quantity?: number;
  size?: RedoBadgeSize;
  type?: RedoBadgeType;
}) {
  const bufferDays = useLotExpirationBufferDays();
  const expText = formatExpiration(expirationDate);
  const qtyText = quantity != null ? ` × ${quantity}` : "";
  const expired = isLotExpired(expirationDate, bufferDays);
  const text = expText
    ? `${lotNumber}${qtyText} · ${expired ? `EXPIRED ${expText}` : expText}`
    : `${lotNumber}${qtyText}`;
  return (
    <RedoBadge
      color={expired ? "error" : hashLotColor(lotNumber)}
      size={size}
      text={text}
      type={type}
    />
  );
});
